import React, { useRef, useState, useEffect } from "react";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";

interface Point {
	x: number;
	y: number;
}

interface SignaturePadProps {
	width?: number,
	height?: number,
	onEnd?: (result: string | undefined) => void
}

const SignaturePad = ({ width = 300, height = 160, onEnd }: SignaturePadProps) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [drawing, setDrawing] = useState(false);
	const [hasDrawn, setHasDrawn] = useState(false);
	const points = useRef<Point[]>([]);
	const [lastPoint, setLastPoint] = useState<Point | null>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ratio = window.devicePixelRatio || 1;
		canvas.width = width * ratio;
		canvas.height = height * ratio;
		canvas.style.width = `${width}px`;
		canvas.style.height = `${height}px`;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.scale(ratio, ratio);

		// Set white background
		ctx.fillStyle = "#fff";
		ctx.fillRect(0, 0, width, height);

		// Drawing styles
		ctx.lineWidth = 4;
		ctx.lineCap = "round";
		ctx.strokeStyle = "#000";
	}, [width, height]);

	const getPos = (e: MouseEvent | TouchEvent): Point => {
		const canvas = canvasRef.current!;
		const rect = canvas.getBoundingClientRect();

		const clientX =
			"touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
		const clientY =
			"touches" in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

		return {
			x: clientX - rect.left,
			y: clientY - rect.top,
		};
	};

	const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
		e.preventDefault();
		const pos = getPos(e.nativeEvent);
		setDrawing(true);
		setLastPoint(pos);
		points.current = [pos];
		setHasDrawn(true);
	};

	const draw = (e: React.MouseEvent | React.TouchEvent) => {
		if (!drawing) return;
		e.preventDefault();

		const ctx = canvasRef.current?.getContext("2d");
		if (!ctx) return;

		const newPoint = getPos(e.nativeEvent);
		setLastPoint(newPoint);
		points.current.push(newPoint);

		const p1 = points.current[points.current.length - 3];
		const p2 = points.current[points.current.length - 2];
		const p3 = points.current[points.current.length - 1];

		if (p1 && p2 && p3) {
			const xc1 = (p1.x + p2.x) / 2;
			const yc1 = (p1.y + p2.y) / 2;
			const xc2 = (p2.x + p3.x) / 2;
			const yc2 = (p2.y + p3.y) / 2;

			ctx.beginPath();
			ctx.moveTo(xc1, yc1);
			ctx.quadraticCurveTo(p2.x, p2.y, xc2, yc2);
			ctx.stroke();
		}
	};

	const endDrawing = () => {
		if (!drawing) return;
		setDrawing(false);

		if (points.current.length === 1 && lastPoint) {
			const ctx = canvasRef.current?.getContext("2d");
			if (ctx) {
				ctx.beginPath();
				ctx.arc(lastPoint.x, lastPoint.y, ctx.lineWidth / 2, 0, Math.PI * 2);
				ctx.fillStyle = ctx.strokeStyle;
				ctx.fill();
			}
		}

		points.current = [];
		setLastPoint(null);

		if (onEnd) {
			onEnd(exportAsBase64());
		}
	};

	const clear = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		ctx.clearRect(0, 0, width, height);
		ctx.fillStyle = "#fff";
		ctx.fillRect(0, 0, width, height);

		setHasDrawn(false);

		if (onEnd) {
			onEnd(undefined);
		}
	};

	const exportAsBase64 = (): string => {
		return canvasRef.current?.toDataURL("image/png") || "";
	};

	const saveImage = () => {
		const base64 = exportAsBase64();
		const a = document.createElement("a");
		a.href = base64;
		a.download = "signature.png";
		a.click();
	};

	return (
		<div>
			<canvas
				ref={canvasRef}
				style={{ border: "2px dashed #ccc", touchAction: "none" }}
				onMouseDown={startDrawing}
				onMouseMove={draw}
				onMouseUp={endDrawing}
				onMouseLeave={endDrawing}
				onTouchStart={startDrawing}
				onTouchMove={draw}
				onTouchEnd={endDrawing}
			/>
			{hasDrawn && (
				<DeleteForeverRoundedIcon
					onClick={clear}
					style={{
						display: hasDrawn ? "block" : "none",
						color: "white",
						background: "red",
						padding: "4px",
						borderRadius: "4px",
						position: "absolute",
						top: "10px",
						right: "10px",
					}}
				/>
			)}
		</div>
	);
};

export default SignaturePad;
